import { useEffect, useState } from "react";
import {
  Input,
  Spin,
  Button,
  Row,
  Col,
  Space,
  Flex,
  Divider,
  Modal,
  Empty,
} from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  CheckCircleFilled,
  CopyTwoTone,
  PlusOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useGetInfluencers } from "@entities/influencer/lib/hooks/useGetInfluencers";
import {
  ICreateInfluencerRequest,
  IInfluencers,
} from "@shared/lib/types/influencer";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { ToastService } from "@shared/lib/services";
import { useLinkSocialMediaToInfluencer } from "@entities/influencer/lib/hooks/useLinkSocialMediaToInfluencer";
import { InfluencerForm, useCreateInfluencer } from "@entities/influencer";
import { Campaigns } from "@entities/influencer/ui/Campaigns/Campaigns";

interface Props {
  handleRefetch: () => void;
  socialMediaId?: string;
  categoriesToDisplay: string[];
}
export function SelectInfluencer({
  handleRefetch,
  socialMediaId,
  categoriesToDisplay,
}: Props) {
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [influencers, setInfluencers] = useState<IInfluencers[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const { create, isCreating, isCreated } = useCreateInfluencer();
  const handleSubmit = (brandData: ICreateInfluencerRequest) => {
    create({ ...brandData, attachSocialMediaObjectId: socialMediaId || "" });
  };

  const [isModalVisible, setModalVisible] = useState(false);

  const { isLinked, isLinking, linkSocialMediaInfluencer } =
    useLinkSocialMediaToInfluencer();

  useEffect(() => {
    if (isLinked) {
      ToastService.success("Social media linked to influencer");
      handleRefetch();
    }
  }, [isLinked]);

  useEffect(() => {
    if (isCreated) {
      handleRefetch();
      ToastService.success("Influencer created successfully");
    }
  }, [isCreated]);

  // Fetch influencers using the hook
  const {
    influencers: fetchedInfluencers,
    isFetching,
    isError,
  } = useGetInfluencers(
    search,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    limit,
    page
  );

  // Extract data and pagination
  const influencerList = fetchedInfluencers?.data || [];
  const fetchedPagination = fetchedInfluencers?.pagination;

  // When search or page changes, update influencers list
  useEffect(() => {
    if (page === 1) {
      setInfluencers(influencerList);
    } else if (influencerList.length) {
      setInfluencers((prev) => [
        ...prev,
        ...influencerList.filter(
          (inf: any) => !prev.some((p) => p._id === inf._id)
        ),
      ]);
    }
    setHasMore(
      influencerList.length === limit &&
        (fetchedPagination?.totalCount || 0) > influencers.length
    );
    // eslint-disable-next-line
  }, [fetchedInfluencers]);

  // Reset on search
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const fetchMoreInfluencers = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className={styles.selectInfluencerWrap}>
      <Flex justify="space-between">
        <div style={{ fontWeight: 600, fontSize: 24, marginBottom: 8 }}>
          Create Influencer
        </div>
        {/* <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={Boolean(selectedId)}
          style={{ marginBottom: 0 }}
          onClick={() => {
            setModalVisible(true);
            // handle select influencer action here
          }}
        >
          Create Influencer
        </Button> */}
      </Flex>
      <Divider style={{ margin: 16 }} />
      <div style={{ color: "#888", marginBottom: 16 }}>
        Search for an existing influencer (in case the influencer is already
        onboarded with a different social media), or create a new Influencer
      </div>
      <Input.Search
        placeholder="Search by Influencer Name"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onSearch={handleSearch}
        enterButton
        allowClear
        style={{ marginBottom: 16, width: "500px" }}
      />
      {search ? (
        <>
          <div
            id="influencer-infinite-scroll"
            style={{
              maxHeight: 400,
              overflow: "auto",
              border: "1px solid #eee",
              borderRadius: 12,
              padding: 8,
              background: "#fff",
            }}
          >
            {isFetching && page === 1 && (
              <Row justify="center" style={{ margin: "20px auto" }}>
                <Spin />
              </Row>
            )}
            <InfiniteScroll
              dataLength={influencers.length}
              next={fetchMoreInfluencers}
              hasMore={hasMore}
              loader={<Spin />}
              scrollableTarget="influencer-infinite-scroll"
            >
              {influencers.map((inf) => (
                <Row
                  key={inf._id}
                  className={classNames(styles.influencerRow, {
                    [styles.selectedRow]: selectedId === inf._id,
                  })}
                  onClick={() => {
                    if (selectedId === inf._id) {
                      setSelectedId(null);
                    } else {
                      setSelectedId(inf._id);
                    }
                  }}
                >
                  <Col span={2}>
                    <span className={styles.check}>
                      {selectedId === inf._id ? (
                        <CheckCircleFilled
                          style={{ color: "#52c41a", fontSize: 22 }}
                        />
                      ) : (
                        <span className={styles.unchecked} />
                      )}
                    </span>
                  </Col>
                  <Col span={2}>
                    <img
                      src={inf.profilePicUrl}
                      alt={`${inf.firstName} ${inf.lastName}`}
                      className={styles.avatar}
                    />
                  </Col>
                  <Col span={4}>
                    <div className={styles.info}>
                      <div
                        className={styles.name}
                      >{`${inf.firstName} ${inf.lastName}`}</div>

                      <div className={styles.tags}>
                        {(inf.category || []).map((tag: string) => (
                          <span key={tag} className={styles.tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.platforms}>
                      {(inf.socialMedias || []).map((p: any) => (
                        <span
                          key={p._id || p.platform}
                          className={styles.platformTag}
                        >
                          {getPlatformIcon(p.platform)}
                          {p.username || p.userName}{" "}
                          {p.followers ? `(${p.followers})` : ""}
                        </span>
                      ))}
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.contact}>
                      {inf?.phoneNumber && (
                        <Space>
                          <div className={styles.phone}>
                            {inf?.countryCode} {inf?.phoneNumber}{" "}
                          </div>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(
                                `${inf?.countryCode} ${inf.phoneNumber}`
                              );
                              ToastService.success("Copied to clipboard");
                            }}
                            icon={<CopyTwoTone />}
                            style={{ border: 0 }}
                          />
                        </Space>
                      )}
                      {inf.emailId && (
                        <Space>
                          <div className={styles.email}>{inf.emailId}</div>
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(inf.emailId);
                              ToastService.success("Copied to clipboard");
                            }}
                            icon={<CopyTwoTone />}
                            style={{ border: 0 }}
                          />
                        </Space>
                      )}
                    </div>
                  </Col>
                </Row>
              ))}
            </InfiniteScroll>
            {isError && (
              <div style={{ color: "red", textAlign: "center" }}>
                Failed to load influencers.
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
            }}
          >
            <Button
              type="primary"
              disabled={!selectedId}
              loading={isLinking}
              onClick={() => {
                linkSocialMediaInfluencer({
                  socialMediaObjectId: socialMediaId || "",
                  influencerId: selectedId || "",
                });
              }}
            >
              Select Influencer
            </Button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Empty
            description={
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                Search for an existing influencer (in case the influencer is
                already onboarded with a different social media), <br /> or
                create a new Influencer
              </div>
            }
            children={
              <Button
                type="default"
                icon={<PlusOutlined />}
                disabled={Boolean(selectedId)}
                style={{ marginBottom: 0 }}
                onClick={() => {
                  setModalVisible(true);
                  // handle select influencer action here
                }}
              >
                Create Influencer
              </Button>
            }
            image={<></>}
            imageStyle={{
              height: 0,
            }}
          />
        </div>
      )}
      <Divider />
      <Campaigns infId={""} socialMediaId={socialMediaId} />

      <Modal
        title={"Create Influencer"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <InfluencerForm
          initialCategories={categoriesToDisplay}
          isSubmitting={isCreating}
          onSubmit={handleSubmit}
        />
      </Modal>
    </div>
  );
}
